import { lazy } from "react";
import { Navigate, Route, Routes as RootRoutes } from "react-router-dom";

import { suspended } from "components";

import { Guard } from "./components/Guard";

import { AuthLayout } from "../auth/AuthLayout";
import { useAuthContext } from "../auth/AuthProvider";

const ForgotPassword = lazy(async () => import("../auth/components/ForgotPassword"));
const SignIn = lazy(async () => import("../auth/components/SignIn"));
const SSOCallback = lazy(async () => import("../auth/components/SSOCallback"));

const MFAChallenge = lazy(async () => import("../auth/components/MFAChallenge"));
const MFAEnroll = lazy(async () => import("../auth/components/MFAEnroll"));
const UpdatePassword = lazy(async () => import("../auth/components/UpdatePassword"));
const LunaApp = lazy(async () => import("../modules/LunaApp"));

export const Routes = () => {
  const { user, mfa } = useAuthContext();

  const isUserLoggedIn = !!user;

  // An aal1 session with a verified factor (or a new user who hasn't enrolled yet) must
  // never reach the app. SignIn's SIGNED_IN listener may navigate("/") at aal1, but the
  // MFA flags are set in the same state update as `user`, so the guard below bounces to
  // the challenge/enroll screen before LunaApp ever renders.
  const isMfaPending = isUserLoggedIn && (mfa.isChallengeRequired || mfa.isEnrollmentRequired);
  const isFullyAuthenticated = isUserLoggedIn && !isMfaPending;

  const appGuardRedirect = mfa.isChallengeRequired
    ? "/auth/mfa/challenge"
    : mfa.isEnrollmentRequired
      ? "/auth/mfa/enroll"
      : "/auth/signin";

  return (
    <RootRoutes>
      <Route element={<Guard haveAccess={isFullyAuthenticated} redirectPath={appGuardRedirect} />}>
        <Route path="*" element={suspended(<LunaApp />)} />
      </Route>
      <Route
        path="/auth/mfa/*"
        element={
          <Guard haveAccess={isMfaPending} redirectPath="/">
            <AuthLayout />
          </Guard>
        }>
        <Route path="challenge" element={suspended(<MFAChallenge />)} />
        <Route path="enroll" element={suspended(<MFAEnroll />)} />
        <Route
          path="*"
          element={<Navigate to={mfa.isChallengeRequired ? "challenge" : "enroll"} replace />}
        />
      </Route>
      <Route
        path="/auth/*"
        element={
          <Guard haveAccess={!isUserLoggedIn} redirectPath="/">
            <AuthLayout />
          </Guard>
        }>
        <Route path="signin" element={suspended(<SignIn />)} />
        <Route path="forgot" element={suspended(<ForgotPassword />)} />
        <Route path="*" element={<Navigate to="signin" replace />} />
      </Route>
      <Route path="/account/update-password" element={suspended(<UpdatePassword />)} />
      <Route path="/auth/sso/callback" element={suspended(<SSOCallback />)} />
    </RootRoutes>
  );
};
